/* eslint-env browser */
// Driver documents › Clearance letter (wave 3, W2_Portal_Settings look): pick
// the driver, fill in the letter (sub city, city, dates in Addis Ababa time
// on a 12-hour clock), check the amount the wallet gives, then generate.
//
// Kept exactly as before:
// - the reads: when a driver is picked, ONE read of their wallet history
//   (walletHistoryRef) and ONE indexed read of their bookings
//   (bookingListRef(uid, 'driver')); everything after that is computed here;
// - the amount: the DEBIT entries tied to COMPLETE / PAID bookings inside
//   the date range (top-ups, transfers and unverified entries excluded);
//   only admin and finance may change it, and a changed amount needs a
//   justification and is flagged in the audit record;
// - the effects, in the same order: generateDriverClearanceLetter, then
//   updateUserFields(driver, { approved: false, adminApprovedTrue: false,
//   driverActiveStatus: false, driverOfflineAt }) (a partial write: the
//   driver is leaving), then saveLetterRecord with the same fields. The dates
//   go to the letter and the record in the same text formats as before
//   ("YYYY-MM-DD" and "YYYY-MM-DDTHH:mm"), now as Addis Ababa wall time.
// New: a confirmation that names the driver and the amount before the letter
// is generated (it deactivates the account), and errors under their fields.
import React, { useState, useEffect, useContext, useMemo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { api, FirebaseContext } from 'common';
import { get } from 'firebase/database';
import { generateDriverClearanceLetter } from '../utils/driverClearanceLetterGenerator';
import { saveLetterRecord } from '../utils/letterRecords';
import { tokens as c } from '../theme';
import { InlineMessage, confirmDialog } from '../components/feedback';
import {
  AddisDateTimeField,
  DetailCard,
  FieldError,
  FieldLabel,
  HintIcon,
  InfoRow,
  SectionCard,
  StatusChip,
  addisPartsToMs,
  formatMoney,
  formatPhoneDisplay,
  msToAddisParts,
  toMs,
} from '../components/modern';
import { DriverPicker, LETTER_LANGUAGES, driverOptionsOf } from './DriverLetterGenerator';

const roundTo = (n, d) => {
  const f = Math.pow(10, d);
  return Math.round((Number(n) + Number.EPSILON) * f) / f;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const pad2 = (n) => (n < 10 ? `0${n}` : String(n));
// Midnight (Addis Ababa) of the day `ms` falls in.
const addisDayStart = (ms) => {
  const p = msToAddisParts(ms);
  return addisPartsToMs({ y: p.y, m: p.m, d: p.d, h: 0, mi: 0 });
};
// The texts the letter and its record take, as before: "2026-10-28" and
// "2026-10-28T14:30" (Addis Ababa wall time).
const addisDateText = (ms) => {
  if (ms === null || ms === undefined) return '';
  const p = msToAddisParts(ms);
  return `${p.y}-${pad2(p.m)}-${pad2(p.d)}`;
};
const addisDateTimeText = (ms) => {
  if (ms === null || ms === undefined) return '';
  const p = msToAddisParts(ms);
  return `${p.y}-${pad2(p.m)}-${pad2(p.d)}T${pad2(p.h)}:${pad2(p.mi)}`;
};

// The 11 sub-cities (kifle ketema) of Addis Ababa — offered as dropdown
// suggestions for the clearance "To:" line. The field is freeSolo, so an
// operator can still type any other value the driver provides.
const ADDIS_SUB_CITIES = ['Addis Ketema', 'Akaky Kaliti', 'Arada', 'Bole', 'Gullele', 'Kirkos', 'Kolfe Keranio', 'Lemi Kura', 'Lideta', 'Nifas Silk-Lafto', 'Yeka'];

// Top-up credits are money ADDED to the driver (admin add-money → topUpBalance,
// or an internal wallet→top-up transfer) — not earnings from service, so they
// are excluded from the clearance amount. In the ledger these carry a txRef that
// starts with "wallet" — e.g. "wallet--OhtAZ5h5ci4l7sNOMwH-ODNX" (admin top-up)
// or "wallet_to_topup_transfer" — whereas trip earnings use the booking id and
// withdrawals use a timestamp. (`operatorUid` is also checked as a backstop.)
// Matching the transfer's txRef excludes BOTH its debit and credit legs, so the
// internal move stays net-zero.
const isTopUp = (tx) => {
  const ref = typeof tx.txRef === 'string' ? tx.txRef : '';
  return ref.indexOf('wallet') === 0 || !!tx.operatorUid;
};

// "Super solid" earnings filter: a ledger entry only counts toward earnings when
// its txRef is an actual booking id (Firebase push key — starts with "-" and is
// ~20 chars, e.g. "-Oq9Tl47Yey3aUVK_BHk"). Trip credits/debits carry the booking
// id; top-ups carry "wallet…", withdrawals carry a timestamp, miles carry
// "RedeemMile" — all of which are correctly excluded.
const isBookingId = (ref) => typeof ref === 'string' && /^-[A-Za-z0-9_-]{18,}$/.test(ref);

// Only bookings in a settled state count toward earnings.
const VALID_BOOKING_STATUSES = { COMPLETE: true, PAID: true };

// Clearance amount from the wallet history. Per Finance, this tallies the DEBIT
// entries (not credits), bounded by the [fromMs, uptoMs] window and validated
// against the driver's bookings (statusById: bookingId → status).
//  • total      = sum of booking-tied DEBITS whose booking exists and is
//    COMPLETE/PAID — the figure printed on the letter.
//  • topUp      = top-up / internal-transfer legs (txRef "wallet…") — excluded.
//  • unverified = booking-id debits whose booking is missing or not
//    COMPLETE/PAID — excluded, surfaced so staff can see why a figure is low.
//  • count      = number of validated debit entries tallied.
const computeWallet = (txns, fromMs, uptoMs, statusById) => {
  let total = 0;
  let topUp = 0;
  let count = 0;
  let unverified = 0;
  (txns || []).forEach((tx) => {
    if (tx.type !== 'Debit') return; // Finance tallies debits — ignore credits
    const d = Number(tx.date);
    if (!Number.isFinite(d)) return;
    if (fromMs != null && d < fromMs) return;
    if (uptoMs != null && d > uptoMs) return;
    const amt = parseFloat(tx.amount) || 0;
    if (isTopUp(tx)) {
      topUp += amt;
      return;
    } // top-up / transfer leg — excluded
    if (!isBookingId(tx.txRef)) return; // only real booking-tied debits count
    // Lookup validation: the booking must exist and be COMPLETE or PAID.
    const status = statusById ? statusById[tx.txRef] : undefined;
    if (!status || !VALID_BOOKING_STATUSES[status]) {
      unverified += amt;
      return;
    }
    total += amt;
    count += 1;
  });
  return { total, topUp, count, unverified };
};

// Tab 2 of the Driver Documents centre. Produces the sub-city service & clearance
// letter, with the amount computed from the driver's wallet ledger over the
// selected date range. Only admin/finance may override the computed figure, and
// overrides are flagged with a justification + deviation in the audit log.
export default function DriverClearanceLetter() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { fetchUsersOnce } = api;
  const firebase = useContext(FirebaseContext);

  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);

  const role = auth.profile?.usertype;
  const canOverride = role === 'admin' || role === 'finance';
  const decimals = settings && settings.decimal != null ? settings.decimal : 2;
  const allowPrivate = !!(settings && settings.AllowCriticalEditsAdmin);

  // Sub city is free-form (provided per driver) with Addis Ababa sub-cities as
  // dropdown suggestions; city defaults to Addis Ababa but stays editable.
  const [subCity, setSubCity] = useState('');
  const [city, setCity] = useState('Addis Ababa');

  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Date range — defaults to [driver.createdAt, today], both editable by anyone.
  // Doubles as the letter's registration period AND the wallet accounting window.
  // Held as instants (the start of the Addis Ababa day); the termination date
  // is a date and time.
  const [fromDay, setFromDay] = useState(null);
  const [uptoDay, setUptoDay] = useState(() => addisDayStart(Date.now()));
  const [terminationAt, setTerminationAt] = useState(() => Date.now());

  // Wallet ledger + the driver's booking statuses (both fetched once per driver).
  const [walletTxns, setWalletTxns] = useState([]);
  const [bookingStatusById, setBookingStatusById] = useState({});
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [amount, setAmount] = useState('');
  const [amountTouched, setAmountTouched] = useState(false); // admin/finance override
  const [justification, setJustification] = useState('');

  // The pack's words when the key exists, else the English default.
  const label = (key, fallback, opts) => t(key, { defaultValue: fallback, ...(opts || {}) });

  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  useEffect(() => {
    if (staticusers) {
      setDrivers(driverOptionsOf(staticusers, t, allowPrivate));
    }
  }, [staticusers, t, allowPrivate]);

  // The texts the letter and the record get (same formats as before).
  const fromDate = addisDateText(fromDay);
  const uptoDate = addisDateText(uptoDay);
  const terminationDate = addisDateTimeText(terminationAt);

  // Date-range → epoch bounds (inclusive whole Addis Ababa days).
  const fromMs = fromDay != null ? addisDayStart(fromDay) : null;
  const uptoMs = uptoDay != null ? addisDayStart(uptoDay) + DAY_MS - 1 : null;

  const computed = useMemo(() => computeWallet(walletTxns, fromMs, uptoMs, bookingStatusById), [walletTxns, fromMs, uptoMs, bookingStatusById]);
  const computedAmount = roundTo(computed.total, decimals);

  // Keep the amount synced to the computed figure unless an admin/finance user
  // has explicitly overridden it (amountTouched). Changing the date range or
  // driver clears the override and re-syncs.
  useEffect(() => {
    if (!amountTouched) setAmount(String(computedAmount));
  }, [computedAmount, amountTouched]);

  const handleDriverSelect = async (event, newValue) => {
    setSelectedDriver(newValue);
    setAmountTouched(false);
    setJustification('');
    setWalletTxns([]);
    setBookingStatusById({});
    setError('');
    setFieldErrors({});
    if (!newValue) {
      setFromDay(null);
      return;
    }
    const created = toMs(newValue.createdAt);
    setFromDay(created !== null ? addisDayStart(created) : null);
    setUptoDay(addisDayStart(Date.now()));
    // Fetch the driver's wallet ledger AND their bookings once (recomputation on
    // date changes is local). The booking statuses validate which credits are
    // real, completed earnings. bookingListRef(uid,'driver') is a single indexed
    // query (bookings/driver index), scoped to this one driver.
    const driverId = newValue.id || newValue.uid;
    if (!driverId) return;
    setLoadingWallet(true);
    try {
      const [walletSnap, bookingsSnap] = await Promise.all([
        firebase.walletHistoryRef ? get(firebase.walletHistoryRef(driverId)) : Promise.resolve(null),
        firebase.bookingListRef ? get(firebase.bookingListRef(driverId, 'driver')) : Promise.resolve(null),
      ]);
      const wVal = (walletSnap && walletSnap.val()) || {};
      setWalletTxns(Object.keys(wVal).map((k) => wVal[k]));
      const bVal = (bookingsSnap && bookingsSnap.val()) || {};
      const statusMap = {};
      Object.keys(bVal).forEach((k) => {
        statusMap[k] = bVal[k] && bVal[k].status;
      });
      setBookingStatusById(statusMap);
    } catch (e) {
      console.error('Failed to load wallet/bookings:', e);
      setWalletTxns([]);
      setBookingStatusById({});
    } finally {
      setLoadingWallet(false);
    }
  };

  // Changing either bound re-syncs the amount to the freshly computed value.
  const handleFromDate = (v) => {
    setFromDay(v);
    setAmountTouched(false);
  };
  const handleUptoDate = (v) => {
    setUptoDay(v);
    setAmountTouched(false);
  };

  const handleAmountChange = (v) => {
    if (!canOverride) return;
    setAmount(v);
    setAmountTouched(true);
    setFieldErrors((prev) => ({ ...prev, amount: undefined }));
  };

  const finalAmount = amount;
  const isOverridden =
    canOverride && amountTouched && finalAmount !== '' && Math.abs((parseFloat(finalAmount) || 0) - computedAmount) > Math.pow(10, -decimals) / 2;
  const deviation = roundTo((parseFloat(finalAmount) || 0) - computedAmount, decimals);

  const money = (n) => formatMoney(n, settings, true);
  const driverName = selectedDriver ? `${selectedDriver.firstName || ''} ${selectedDriver.lastName || ''}`.trim() : '';

  const handleGenerate = async () => {
    if (!selectedDriver) {
      setError(label('please_select_driver', 'Please select a driver first'));
      return;
    }
    const errs = {};
    if (!subCity.trim()) errs.subCity = label('sub_city_required', 'Please enter or select the sub city');
    if (`${finalAmount}`.trim() === '' || isNaN(parseFloat(finalAmount))) errs.amount = label('amount_required', 'A valid amount is required');
    if (isOverridden && !justification.trim()) {
      errs.justification = label('override_justification_required', 'A justification is required when overriding the computed amount.');
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      const first = errs.subCity ? 'clearance-subcity' : errs.amount ? 'clearance-amount' : 'clearance-justification';
      const el = typeof document !== 'undefined' ? document.getElementById(first) : null;
      if (el && typeof el.focus === 'function') el.focus();
      return;
    }

    // The letter also sets the driver's account to not approved and offline:
    // ask first, naming the driver and the amount.
    const ok = await confirmDialog({
      title: label('clearance_confirm_title', 'Issue a clearance letter for {{name}}?', { name: driverName }),
      body: label('clearance_confirm_body', 'The letter shows {{amount}}. The driver’s account will be set to not approved and offline.', {
        amount: money(finalAmount),
      }),
      confirmLabel: label('generate_clearance_letter', 'Generate Clearance Letter'),
      cancelLabel: label('confirm_cancel', 'Cancel'),
      destructive: true,
    });
    if (!ok) return;

    setGenerating(true);
    setError('');
    setSuccess('');

    const clearanceData = { subCity, city, fromDate, uptoDate, amount: finalAmount, terminationDate };

    try {
      const refNo = await generateDriverClearanceLetter(selectedDriver, clearanceData, selectedLanguage);

      // A clearance letter means the driver is leaving — deactivate the account:
      // approved=false (no longer an approved driver) and driverActiveStatus=false
      // (no longer working), written as a PARTIAL update. The old full-record
      // editUser() replaced the node from the cached combo row and wiped every
      // field written server-side since. driverOfflineAt marks the deliberate
      // offline for the auto-online pass. adminApprovedTrue=false as well: the
      // role switch trusts that flag, so a cleared driver who switches to
      // customer and back must not come back approved.
      let deactivated = false;
      try {
        const dId = selectedDriver.id || selectedDriver.uid;
        dispatch(api.updateUserFields(dId, { approved: false, adminApprovedTrue: false, driverActiveStatus: false, driverOfflineAt: Date.now() }));
        deactivated = true;
      } catch (deErr) {
        console.error('Failed to set driver to not-approved after clearance:', deErr);
      }

      const profile = auth.profile || {};
      await saveLetterRecord(firebase, {
        type: 'clearance',
        refNo: refNo || null,
        language: selectedLanguage,
        driverId: selectedDriver.id || selectedDriver.uid || null,
        driverName: `${selectedDriver.firstName || ''} ${selectedDriver.lastName || ''}`.trim(),
        driverMobile: selectedDriver.mobile || null,
        driverEmail: selectedDriver.email || null,
        tin: selectedDriver.tin || null,
        plate: selectedDriver.vehicleNumber || null,
        subCity: subCity || null,
        city: city || null,
        fromDate: fromDate || null,
        uptoDate: uptoDate || null,
        terminationDate: terminationDate || null,
        amount: `${finalAmount}`,
        // Debit-tally snapshot — lets finance reconcile later.
        computedDebitTotal: computedAmount,
        computedExcludedTopUp: roundTo(computed.topUp, decimals),
        computedUnverified: roundTo(computed.unverified, decimals),
        debitEntryCount: computed.count,
        // Fraud flag: overridden amounts carry the deviation + justification.
        amountOverridden: !!isOverridden,
        amountDeviation: isOverridden ? deviation : 0,
        overrideJustification: isOverridden ? justification.trim() : null,
        generatedByUid: profile.uid || null,
        generatedByName: `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || null,
        generatedByEmail: profile.email || null,
        generatedByRole: profile.usertype || null,
        // Clearance deactivates the driver — record it for the audit trail.
        driverApprovedSetFalse: deactivated,
        generatedAt: new Date().getTime(),
      });
      setSuccess(
        `${label('clearance_letter_generated', 'Clearance letter generated for')} ${selectedDriver.firstName} ${selectedDriver.lastName}.` +
          (deactivated ? ` ${label('driver_deactivated_note', 'The driver account has been set to NOT approved (no longer working).')}` : '')
      );
    } catch (e) {
      console.error('Error generating clearance letter:', e);
      setError(label('clearance_generate_failed', 'Failed to generate clearance letter. Please try again.'));
    } finally {
      setGenerating(false);
    }
  };

  const hidden = t('hidden_demo');
  const amountHint = canOverride
    ? label('amount_override_hint', 'Computed from wallet. Editable — overrides require a justification.')
    : label('amount_locked_hint', 'Auto-computed from wallet. Only admin/finance can override.');
  const fieldBox = { minWidth: 0 };

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error ? (
        <InlineMessage kind="error" onClose={() => setError('')}>
          {error}
        </InlineMessage>
      ) : null}
      {success ? (
        <InlineMessage kind="success" onClose={() => setSuccess('')}>
          {success}
        </InlineMessage>
      ) : null}

      <SectionCard icon={<PersonSearchOutlinedIcon />} hue="info" title={label('driver_clearance_letter', 'Driver Clearance Letter Generator')} id="clearance-driver">
        <Box sx={{ pt: 1 }}>
          <DriverPicker id="clearance-driver-picker" label={label('select_driver', 'Select Driver')} drivers={drivers} value={selectedDriver} onChange={handleDriverSelect} />
        </Box>
      </SectionCard>

      {selectedDriver ? (
        <>
          <DetailCard icon={<BadgeOutlinedIcon />} hue="info" title={label('driver_details', 'Driver Details')}>
            <InfoRow label={label('name', 'Name')}>{driverName}</InfoRow>
            <InfoRow label={label('mobile', 'Mobile')}>{allowPrivate ? formatPhoneDisplay(selectedDriver.mobile) || '—' : hidden}</InfoRow>
            <InfoRow label={t('tin')}>{allowPrivate ? selectedDriver.tin || '—' : hidden}</InfoRow>
            <InfoRow label={label('plate_number', 'Plate Number')}>{selectedDriver.vehicleNumber || '—'}</InfoRow>
          </DetailCard>

          <SectionCard icon={<PlaceOutlinedIcon />} hue="accent" title={label('clearance_letter', 'Clearance Letter')} id="clearance-letter">
            <Box sx={{ display: 'grid', gap: 2, pt: 1.5, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
              {/* Sub city — free-form with Addis Ababa sub-cities as suggestions. */}
              <Box sx={fieldBox}>
                <FieldLabel htmlFor="clearance-subcity">{label('sub_city', 'Sub City')}</FieldLabel>
                <Autocomplete
                  id="clearance-subcity"
                  freeSolo
                  options={ADDIS_SUB_CITIES}
                  inputValue={subCity}
                  onInputChange={(e, v) => {
                    setSubCity(v);
                    if (v && v.trim()) setFieldErrors((prev) => ({ ...prev, subCity: undefined }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      error={!!fieldErrors.subCity}
                      inputProps={{ ...params.inputProps, 'aria-describedby': fieldErrors.subCity ? 'clearance-subcity-error' : undefined }}
                    />
                  )}
                />
                <FieldError id="clearance-subcity-error">{fieldErrors.subCity}</FieldError>
              </Box>
              <Box sx={fieldBox}>
                <FieldLabel htmlFor="clearance-city">{label('city_label', 'City')}</FieldLabel>
                <TextField id="clearance-city" fullWidth value={city} onChange={(e) => setCity(e.target.value)} />
              </Box>
              {/* Date range — bounds the wallet accounting AND the letter period. */}
              <Typography component="h3" sx={{ gridColumn: '1 / -1', fontSize: 15, fontWeight: 700, color: c.INK, mt: 1 }}>
                {label('accounting_period', 'Accounting Period (Date Range)')}
              </Typography>
              <Box sx={fieldBox}>
                <FieldLabel htmlFor="clearance-from">{label('clearance_from_date', 'Registered From')}</FieldLabel>
                <AddisDateTimeField id="clearance-from" value={fromDay} onChange={handleFromDate} dateOnly />
              </Box>
              <Box sx={fieldBox}>
                <FieldLabel htmlFor="clearance-upto">{label('clearance_upto_date', 'Up To')}</FieldLabel>
                <AddisDateTimeField id="clearance-upto" value={uptoDay} onChange={handleUptoDate} dateOnly />
              </Box>
              <Box sx={{ ...fieldBox, gridColumn: { sm: '1 / -1' } }}>
                <FieldLabel htmlFor="clearance-termination">{label('termination_date', 'Termination Date')}</FieldLabel>
                <AddisDateTimeField id="clearance-termination" value={terminationAt} onChange={setTerminationAt} minuteStep={1} />
              </Box>
            </Box>
          </SectionCard>

          <SectionCard
            icon={<AccountBalanceWalletOutlinedIcon />}
            hue="success"
            title={label('wallet_accounting', 'Wallet Accounting (within date range)')}
            id="clearance-wallet"
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {loadingWallet ? <CircularProgress size={18} aria-label={label('loading', 'Loading…')} /> : null}
                <HintIcon
                  id="clearance-debit-note"
                  hint={label(
                    'debit_note',
                    'Tallied from booking DEBIT entries on COMPLETED/PAID bookings only (per Finance). Top-ups and unverified entries are excluded.'
                  )}
                />
              </Box>
            }
          >
            <Box component="dl" sx={{ m: 0, mb: 2 }} aria-describedby="clearance-debit-note">
              <InfoRow label={label('total_debited', 'Total (Debit)')}>
                <Box component="span" sx={{ fontWeight: 700, color: c.SUCCESS_TEXT, fontVariantNumeric: 'tabular-nums' }}>
                  {money(roundTo(computed.total, decimals))}
                </Box>
              </InfoRow>
              <InfoRow label={label('debit_entries', 'Debit Entries')}>{computed.count}</InfoRow>
              <InfoRow label={label('excluded_topups', 'Excluded (top-up/transfer)')}>{money(roundTo(computed.topUp, decimals))}</InfoRow>
              {computed.unverified > 0 ? (
                <InfoRow label={label('unverified_excluded', 'Excluded (booking not COMPLETE/PAID or not found)')}>
                  <Box component="span" sx={{ color: c.AMBER_TEXT, fontWeight: 600 }}>
                    {money(roundTo(computed.unverified, decimals))}
                  </Box>
                </InfoRow>
              ) : null}
            </Box>

            {/* Amount — editable only by admin/finance, with override justification. */}
            <Box>
              <Box sx={{ maxWidth: 280, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FieldLabel htmlFor="clearance-amount" sx={{ mb: 0.75 }}>
                    {label('amount_earned', 'Amount on Letter')}
                  </FieldLabel>
                  <Box sx={{ mb: 0.75 }}>
                    <HintIcon id="clearance-amount-hint" hint={amountHint} />
                  </Box>
                </Box>
                <TextField
                  id="clearance-amount"
                  fullWidth
                  type="number"
                  value={amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  error={!!fieldErrors.amount}
                  inputProps={{
                    step: '0.01',
                    min: '0',
                    inputMode: 'decimal',
                    readOnly: !canOverride,
                    'aria-describedby': `clearance-amount-hint${fieldErrors.amount ? ' clearance-amount-error' : ''}`,
                  }}
                  disabled={!canOverride}
                />
                <FieldError id="clearance-amount-error">{fieldErrors.amount}</FieldError>
              </Box>
              {isOverridden ? (
                <StatusChip
                  tone="warning"
                  icon={<WarningAmberRoundedIcon />}
                  label={`${label('overridden', 'Overridden')} · ${label('deviation', 'Deviation')}: ${money(deviation)}`}
                  sx={{ mt: 1 }}
                />
              ) : null}
            </Box>

            {isOverridden ? (
              <Box sx={{ mt: 2 }}>
                <FieldLabel htmlFor="clearance-justification">{label('override_justification', 'Justification for override')}</FieldLabel>
                <TextField
                  id="clearance-justification"
                  fullWidth
                  multiline
                  minRows={2}
                  required
                  value={justification}
                  error={!!fieldErrors.justification}
                  onChange={(e) => {
                    setJustification(e.target.value);
                    if (e.target.value.trim()) setFieldErrors((prev) => ({ ...prev, justification: undefined }));
                  }}
                  inputProps={{ 'aria-describedby': fieldErrors.justification ? 'clearance-justification-error' : undefined }}
                />
                <FieldError id="clearance-justification-error">{fieldErrors.justification}</FieldError>
              </Box>
            ) : null}

            <Box sx={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2, mt: 2.5, pt: 2, borderTop: `1px solid ${c.OUTLINE}` }}>
              <Box sx={{ flex: '1 1 240px', maxWidth: 360 }}>
                <FieldLabel htmlFor="clearance-language">{label('lang', 'Language')}</FieldLabel>
                <TextField id="clearance-language" select fullWidth value={selectedLanguage} onChange={(e) => setSelectedLanguage(e.target.value)}>
                  {LETTER_LANGUAGES.map((lang) => (
                    <MenuItem key={lang.code} value={lang.code}>
                      {lang.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Button
                variant="contained"
                size="large"
                startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <DownloadRoundedIcon />}
                onClick={handleGenerate}
                disabled={generating || loadingWallet}
                sx={{ marginInlineStart: 'auto' }}
              >
                {generating ? label('generating', 'Generating…') : label('generate_clearance_letter', 'Generate Clearance Letter')}
              </Button>
            </Box>
          </SectionCard>
        </>
      ) : null}
    </Box>
  );
}
